import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePortal } from '@/contexts/PortalContext';
import { 
  FileText, 
  Plus, 
  Users, 
  CheckCircle,
  Clock,
  ChevronRight,
  Loader2,
  BookOpen,
  Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InstructorAssignments() {
  const { token } = useAuth();
  const { isInstructor } = usePortal();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        // Get all courses (instructor view)
        const response = await fetch(`${API_URL}/api/portal/courses?status=active`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // For each course, fetch assignments
          const coursesWithAssignments = await Promise.all(
            data.courses.map(async (course) => {
              try {
                const assignResponse = await fetch(
                  `${API_URL}/api/portal/assignments/instructor/course/${course.course_id}`,
                  {
                    credentials: 'include',
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                  }
                );
                
                if (assignResponse.ok) {
                  const assignData = await assignResponse.json();
                  return {
                    ...course,
                    assignments: assignData.assignments || [],
                    totalAssignments: assignData.total || 0
                  };
                }
              } catch (err) {
                console.error(`Failed to fetch assignments for ${course.course_id}:`, err);
              }
              return { ...course, assignments: [], totalAssignments: 0 };
            })
          );
          
          setCourses(coursesWithAssignments.filter(c => c.assignments.length > 0 || isInstructor));
        }
      } catch (err) {
        console.error('Failed to fetch courses:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isInstructor) {
      fetchCourses();
    } else {
      setLoading(false);
    }
  }, [token, isInstructor]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No due date';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-gray-500">Loading assignments...</p>
        </div>
      </div>
    );
  }

  if (!isInstructor) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-2">Instructor Access Required</h3>
          <p className="text-gray-500">This page is only accessible to instructors.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="instructor-assignments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Manage Assignments</h1>
          <p className="text-gray-500 mt-1">Create, edit, and grade assignments across your courses</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/portal/instructor/gradebook">
          <Card className="hover:shadow-md transition-all cursor-pointer h-full">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Gradebook</h3>
                <p className="text-sm text-gray-500">View all grades</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/portal/instructor/submissions">
          <Card className="hover:shadow-md transition-all cursor-pointer h-full">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Submissions</h3>
                <p className="text-sm text-gray-500">Review & grade</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>
        
        <Card className="opacity-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Plus className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Create Assignment</h3>
              <p className="text-sm text-gray-500">Coming soon</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Courses with Assignments */}
      {courses.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">No Courses Found</h3>
            <p className="text-gray-500">You don't have any courses assigned yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {courses.map((course) => (
            <Card key={course.course_id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className="bg-blue-100 text-blue-700 mb-2">{course.code}</Badge>
                    <CardTitle className="text-lg">{course.title}</CardTitle>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">{course.totalAssignments}</div>
                    <div className="text-sm text-gray-500">assignments</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {course.assignments.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4">No assignments created yet.</p>
                ) : (
                  <div className="divide-y">
                    {course.assignments.map((assignment) => (
                      <Link 
                        key={assignment.assignment_id}
                        to={`/portal/instructor/submissions?assignment=${assignment.assignment_id}`}
                        className="block py-3 hover:bg-gray-50 -mx-4 px-4 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-gray-900 truncate">
                                {assignment.title}
                              </h4>
                              {!assignment.is_published && (
                                <Badge className="bg-gray-100 text-gray-600">Draft</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Due {formatDate(assignment.due_date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {assignment.total_submissions}/{assignment.enrolled_count} submitted
                              </span>
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                {assignment.graded_count} graded
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {assignment.points_possible} pts
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
